<template>
  <v-container fluid :class="isEditing ? 'tract-form-visits' : 'tract-detail-visits'">
    <v-row v-if="mobileCompatible" no-gutters justify="end">
      <v-col cols="auto">
        <Icon
        icon="mdi-plus"
        iconColor="primary"
        elementId="log-visit"
        :tooltipText="$t('logVisit')"
        :small="false"
        @icon-clicked="openVisitForm()"/>
      </v-col>
    </v-row>
    <DataTable
    :items="visits"
    :headers="headers"
    :actions="tableActions"
    :loading="loading"
    :customCells="customColumns"
    :hideHeader="mobileCompatible"
    :abbreviateHeaders="mobileCompatible"
    htmlIdentifier="tract-visits"
    defaultSort="completedAt"
    sortDesc
    showPointerCursor
    @refresh="refresh"
    @create="openVisitForm()"
    @click:row="openDetail($event.data)">
      <template #completedAt="{item}">
        <span>{{ formatDate(item.completedAt) }}</span>
      </template>
      <template #notes="{item}">
        <span class="d-inline-block text-truncate" style="max-width: 320px;">{{ item.notes }}</span>
      </template>
      <template #actions="{item}">
        <Icon
        icon="mdi-pencil"
        iconColor="success"
        elementId="edit-visit"
        :tooltipText="$t('editVisit')"
        @icon-clicked="openVisitForm(item)"/>
        <Icon
        :icon="IconHelpers.getIconForObjectType('tract')"
        iconColor="primary"
        elementId="move-visit"
        :tooltipText="$t('moveVisit')"
        @icon-clicked="moveVisitClicked(item)"/>
        <Icon
        icon="mdi-delete-forever"
        iconColor="error"
        elementId="delete-visit"
        :tooltipText="$t('deleteVisit')"
        @icon-clicked="deleteVisitClicked(item)"/>
      </template>
    </DataTable>

    <TractVisitDetailDialog ref="visitDetail" :tract="tract" @edit="onDetailEdit"/>

    <Dialog
    :stateId="formDialogId"
    maxWidth="52em"
    @dialog-closing="closeVisitForm"
    @close="closeVisitForm">
      <TractVisitForm
      v-if="visitForm"
      :tract="tract"
      :propVisit="propVisit"
      @visit-mutated="visitMutated"/>
    </Dialog>

    <Dialog
    :stateId="moveDialogId"
    maxWidth="500px"
    @dialog-closing="closeMoveDialog"
    @close="closeMoveDialog">
      <TractVisitMoveDialog
      v-if="moveDialog"
      :tract="tract"
      :visit="visitForMove"
      @move="moveVisit"/>
    </Dialog>

    <v-dialog v-model="deleteConfirmation" width="400px">
      <ConfirmDialog
      v-if="deleteConfirmation"
      :title="$t('deleteVisit')"
      :body="$t('deleteVisitBody', { type: visitForDelete.tractVisitTypeName, date: formatDate(visitForDelete.completedAt) })"
      :confirmText="$t('deleteVisit')"
      @confirm="deleteVisit"
      @cancel="deleteConfirmation = false"/>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import TractVisitHeaders from '@/headers/TractVisit.js'
import IconHelpers from '@/utils/IconHelpers'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { tractVisitFilesClient } from '@/utils/TractVisitFiles'
import { uniqueDialogId } from '@/utils/componentHelpers'

export default {
  name: 'TractVisits',

  components: {
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    ConfirmDialog: () => import('@/components/helper/ConfirmDialog.vue'),
    TractVisitForm: () => import('@/components/tract/tract-visits/TractVisitForm.vue'),
    TractVisitDetailDialog: () => import('@/components/tract/tract-visits/TractVisitDetailDialog.vue'),
    TractVisitMoveDialog: () => import('@/components/tract/tract-visits/TractVisitMoveDialog.vue')
  },

  props: {
    tract: {
      type: Object,
      required: true
    },
    isEditing: {
      type: Boolean,
      required: false,
      default: false
    },
    mobileCompatible: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data: () => ({
    visits: [],
    loading: false,
    visitForm: false,
    propVisit: undefined,
    deleteConfirmation: false,
    visitForDelete: undefined,
    moveDialog: false,
    visitForMove: undefined,
    formDialogId: uniqueDialogId('tract-visit-form'),
    moveDialogId: uniqueDialogId('tract-visit-move'),
    IconHelpers
  }),

  computed: {
    ...mapGetters('tract-visit-type', ['allTractVisitTypes']),
    ...mapGetters('user', ['userInfo']),

    headers () {
      return this.mobileCompatible
        ? TractVisitHeaders.tractVisitMobileHeaders()
        : TractVisitHeaders.tractVisitHeaders()
    },

    mobileMenuOptions () {
      return {
        tab: this.$t('visits'),
        actions: [
          {
            id: 'refresh',
            action: this.refresh
          }
        ]
      }
    },

    customColumns () {
      return [
        'completedAt',
        'notes',
        'actions'
      ].map(n => ({ slotName: n, value: n }))
    },

    tableActions () {
      return [
        {
          actionName: 'refresh',
          icon: 'mdi-refresh',
          text: this.$t('refresh')
        },
        {
          actionName: 'create',
          icon: 'mdi-plus',
          text: this.$t('logVisit'),
          id: 'log-visit'
        }
      ]
    }
  },

  async created () {
    if (this.mobileCompatible) this.$emit('extra-menu-options', this.mobileMenuOptions)
    if (!this.allTractVisitTypes.length) await this.fetchTractVisitTypes()
    this.refresh()
  },

  methods: {
    ...mapActions('tract-visit', ['fetchTractVisits', 'updateTractVisit', 'deleteTractVisit']),
    ...mapActions('tract-visit-type', ['fetchTractVisitTypes']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    async refresh () {
      this.loading = true
      try {
        this.visits = await this.fetchTractVisits(this.tract.tractId)
      } finally {
        this.loading = false
      }
    },

    formatDate (date) {
      return utcToLocalDate(date, 'L - LT')
    },

    openDetail (visit) {
      this.$refs.visitDetail.open(visit)
    },

    onDetailEdit (visit) {
      setTimeout(() => this.openVisitForm(visit), 0)
    },

    openVisitForm (visit) {
      if (!visit && !this.allTractVisitTypes.length) {
        this.setSnackError(this.$t('noTractVisitTypesDefined'))
        return
      }
      this.propVisit = visit
      this.visitForm = true
      this.openOrUpdateDialog(this.formDialogId)
    },

    moveVisitClicked (visit) {
      this.visitForMove = visit
      this.moveDialog = true
      this.openOrUpdateDialog(this.moveDialogId)
    },

    closeMoveDialog () {
      this.closeDialogsAtOrAbove(this.moveDialogId)
      this.moveDialog = false
      this.visitForMove = undefined
    },

    async moveVisit (targetTract) {
      const visit = this.visitForMove
      this.closeMoveDialog()
      await this.updateTractVisit({
        tractId: this.tract.tractId,
        visit: {
          tractVisitId: visit.tractVisitId,
          tractId: targetTract.tractId,
          tractVisitTypeId: visit.tractVisitTypeId,
          completedAt: visit.completedAt,
          notes: visit.notes
        }
      })
      this.setSnack(this.$t('visitMoved', { tract: targetTract.name }))
      this.refresh()
    },

    closeVisitForm () {
      this.closeDialogsAtOrAbove(this.formDialogId)
      this.visitForm = false
      this.propVisit = undefined
    },

    visitMutated () {
      this.closeVisitForm()
      this.refresh()
    },

    deleteVisitClicked (visit) {
      if (visit.createdByUserId !== this.userInfo.applicationUserId) {
        this.setSnackError(this.$t('onlyVisitCreatorCanDelete'))
        return
      }
      this.visitForDelete = visit
      this.deleteConfirmation = true
    },

    async deleteVisit () {
      const visit = this.visitForDelete
      this.deleteConfirmation = false
      await tractVisitFilesClient.deleteAllFiles(this.tract.tractId, visit.tractVisitId)
      await this.deleteTractVisit({ tractId: this.tract.tractId, tractVisitId: visit.tractVisitId })
      this.setSnack(this.$t('visitDeleted'))
      this.refresh()
    }
  }
}
</script>
